import type {
  HouseholdProfilePut,
  HouseholdProfileView,
  ProfileSelfView,
} from '@amumin/api-client';
import {
  canonical,
  canonicalIslamicFixes,
  DAY_SETS,
  defaultIslamicTradition,
  FAITH_PRACTICES,
  FAITHS,
  FOOD_RULES,
  isTradition,
  KITCHENS,
  normalizeMadhab,
  normalizePractices,
  practicesOfFaiths,
  PRACTICES,
  PROFILE_CONSENT_VERSION,
  TRADITIONS,
  traditionKitchens,
  traditionsFromKitchens,
  type DaySet,
  type Faith,
  type FoodRule,
  type IslamicTradition,
  type Kitchen,
  type Madhab,
  type Practice,
  type Tradition,
} from '@amumin/domain/profile';
import { t, type AppMessages } from '@amumin/i18n';

// Model van de profielschermen (wizardstap, Instellingen, ledenblad; docs/DESIGN.md §8.P). Puur:
// geen Preact, geen fetch. Tradities bestaan alleen hier (sessie); opgeslagen worden keukens.

/** Wat een ouder op het scherm kiest (tradities: alleen sessie). */
export interface ProfileDraft {
  traditions: Tradition[];
  /** Keukens via "Anders" (geen traditiecode: Chinees, Mexicaans, ...). */
  otherKitchens: Kitchen[];
  faiths: Faith[] | null;
  foodRules: FoodRule[];
  daySets: DaySet[];
  dayKeysOff: string[];
  icsDays: boolean;
  suggestionAnswered: boolean;
  /** Golf 2: gekozen traditie voor de islamitische dagen; null = volgt de keuken. */
  islamicTradition: IslamicTradition | null;
  /** Golf 2: bevestigde of verschoven islamitische dagen. */
  islamicFixes: Record<string, number>;
  /**
   * Golf 6b: aangevinkte gewoontes bij het geloof (Bidden, Wudu, Moskee(les), Koranles, ...).
   * null = geen geloof gekozen. Een nieuw gekozen geloof begint zonder vinkjes.
   */
  practices: Practice[] | null;
  /**
   * Golf 6c: de mazhab (rechtsschool), alleen bij Islam; null = "weet ik niet / sla over". Nooit
   * afgeleid uit een keuken of traditie.
   */
  madhab: Madhab | null;
}

export const EMPTY_DRAFT: ProfileDraft = {
  traditions: [],
  otherKitchens: [],
  faiths: null,
  foodRules: [],
  daySets: [],
  dayKeysOff: [],
  icsDays: false,
  suggestionAnswered: false,
  islamicTradition: null,
  islamicFixes: {},
  practices: null,
  madhab: null,
};

/** Keukens die niet via een traditie-chip komen (de keuze onder "Anders"). */
export const OTHER_KITCHENS: Kitchen[] = KITCHENS.filter((k) => !isTradition(k));

/** Van de opgeslagen stand naar een draft (Instellingen: chips uit traditionsFromKitchens). */
export function draftFromView(v: HouseholdProfileView | null): ProfileDraft {
  if (!v || !v.set) return { ...EMPTY_DRAFT };
  const kitchens = canonical(v.kitchens, KITCHENS);
  return {
    traditions: traditionsFromKitchens(kitchens),
    otherKitchens: kitchens.filter((k) => !isTradition(k)),
    faiths: v.faiths === null ? null : canonical(v.faiths, FAITHS),
    foodRules: canonical(v.food_rules, FOOD_RULES),
    daySets: canonical(v.day_sets, DAY_SETS),
    dayKeysOff: [...v.day_keys_off],
    icsDays: v.ics_days,
    suggestionAnswered: v.suggestion_answered,
    islamicTradition: v.day_calendar ?? null,
    islamicFixes: { ...(v.day_fixes ?? {}) },
    // Nog niet gevraagd (bestaand huishouden): alles van het gekozen geloof staat aan, precies wat
    // het gezin nu ziet. Opslaan maakt die keuze dan zichtbaar en aanpasbaar.
    practices:
      v.practices !== null && v.practices !== undefined
        ? canonical(v.practices, PRACTICES)
        : v.faiths && v.faiths.length > 0
          ? practicesOfFaiths(canonical(v.faiths, FAITHS))
          : null,
    madhab: normalizeMadhab(
      v.madhab ?? null,
      v.faiths === null ? null : canonical(v.faiths, FAITHS),
    ),
  };
}

/**
 * Geloof aan of uit (golf 6b): de gewoontes van een nieuw gekozen geloof staan UIT (niets
 * voorgevinkt; "een moslim kan ook niet praktiserend zijn"); die van een weggehaald geloof
 * verdwijnen. Zonder geloof: null. Golf 6c: zonder Islam verdwijnt ook de mazhab.
 */
export function withFaiths(
  d: Pick<ProfileDraft, 'practices'> & Partial<Pick<ProfileDraft, 'madhab'>>,
  faiths: Faith[] | null,
): { faiths: Faith[] | null; practices: Practice[] | null; madhab: Madhab | null } {
  const madhab = normalizeMadhab(d.madhab ?? null, faiths);
  if (!faiths || faiths.length === 0) return { faiths, practices: null, madhab };
  const allowed = practicesOfFaiths(faiths);
  return {
    faiths,
    practices: (d.practices ?? []).filter((p) => allowed.indexOf(p) >= 0),
    madhab,
  };
}

/** Eén gewoonte aan of uit. */
export function togglePractice(list: ReadonlyArray<Practice> | null, v: Practice): Practice[] {
  return toggle(list ?? [], v, PRACTICES);
}

/**
 * De traditie die nu telt: de keuze in dit concept, anders de vaste standaard (nooit meer uit de
 * keukens; review golf 6c r1).
 */
export function draftTradition(d: ProfileDraft): IslamicTradition {
  return d.islamicTradition ?? defaultIslamicTradition();
}

/** Keukens die we opslaan: de traditie-chips plus (alleen met "Anders") de andere keukens. */
export function draftKitchens(d: ProfileDraft): Kitchen[] {
  const withOther = d.traditions.indexOf('other') >= 0 ? d.otherKitchens : [];
  return canonical([...traditionKitchens(d.traditions), ...withOther], KITCHENS);
}

/** Is er iets gekozen? Anders slaan we niets op (en vragen we geen toestemming). */
export function draftHasContent(d: ProfileDraft): boolean {
  return (
    draftKitchens(d).length > 0 ||
    d.faiths !== null ||
    d.foodRules.length > 0 ||
    d.daySets.length > 0
  );
}

/** De PUT: precies wat de ouder koos, geen tradities, niets afgeleid. */
export function draftToPut(
  d: ProfileDraft,
  version: number | null,
  consentLocale: 'nl' | 'en' | null,
): HouseholdProfilePut {
  const daySets = canonical(d.daySets, DAY_SETS);
  return {
    kitchens: draftKitchens(d),
    faiths: d.faiths === null ? null : canonical(d.faiths, FAITHS),
    food_rules: canonical(d.foodRules, FOOD_RULES),
    day_sets: daySets,
    day_keys_off: daySets.length > 0 ? [...d.dayKeysOff] : [],
    ics_days: daySets.length > 0 && d.icsDays,
    suggestion_answered: d.suggestionAnswered,
    // Golf 2: traditie en bevestigingen alleen met de islamitische set aan (0030 CHECK).
    day_calendar: daySets.indexOf('islamic') >= 0 ? d.islamicTradition : null,
    day_fixes:
      daySets.indexOf('islamic') >= 0
        ? canonicalIslamicFixes(d.islamicFixes, draftTradition(d))
        : {},
    // Golf 6b: alleen gewoontes van het gekozen geloof; zonder geloof null. Met een geloof en nog
    // niets aangevinkt: [] (bewust niets), nooit null ("niet gevraagd" is alleen voor oude gezinnen).
    practices: normalizePractices(
      d.practices ?? [],
      d.faiths === null ? null : canonical(d.faiths, FAITHS),
    ),
    // Golf 6c: de mazhab alleen bij Islam (anders null: de server laat hem dan ook weg).
    madhab: normalizeMadhab(d.madhab, d.faiths === null ? null : canonical(d.faiths, FAITHS)),
    ...(consentLocale
      ? {
          consent: {
            accepted: true as const,
            text_version: PROFILE_CONSENT_VERSION,
            locale: consentLocale,
          },
        }
      : {}),
    if_version: version,
  };
}

/** Vergelijkt twee drafts op wat er opgeslagen wordt (voor "Wijzigingen opslaan"). */
export function sameDraft(a: ProfileDraft, b: ProfileDraft): boolean {
  return JSON.stringify(draftToPut(a, null, null)) === JSON.stringify(draftToPut(b, null, null));
}

/**
 * Wat het afwijkingsblad (MemberProfileSheet) nodig heeft: het hele profiel (ouder, GET /profile)
 * of alleen het eigen deel (GET /profile/self, bijvoorbeeld een kind van 16+ met eigen login).
 */
export type MemberSheetView = Pick<
  HouseholdProfileView,
  'faiths' | 'food_rules' | 'members' | 'access' | 'current_text_version'
> & { consent: { text_version: string } | null };

/** GET /profile/self → het blad, met alleen de eigen rij (toegang 'self'). null = niet zelf. */
export function selfSheetView(v: ProfileSelfView | null): MemberSheetView | null {
  if (!v || !v.can_set || !v.member_id) return null;
  return {
    faiths: v.faiths,
    food_rules: v.food_rules,
    members: v.member ? [v.member] : [],
    access: [
      { member_id: v.member_id, access: 'self', lapsed: false, self_confirmed: v.self_confirmed },
    ],
    current_text_version: v.current_text_version,
    consent: v.consent_active ? { text_version: v.current_text_version } : null,
  };
}

/** Toestemming nodig bij opslaan: nog geen, of een oude tekstversie. */
export function needsConsent(
  v: Pick<MemberSheetView, 'consent' | 'current_text_version'> | null,
): boolean {
  return !v || !v.consent || v.consent.text_version !== v.current_text_version;
}

/** Aan/uit in een lijst (canoniek in de volgorde van de woordenlijst). */
export function toggle<T extends string>(
  list: ReadonlyArray<T>,
  v: T,
  vocab: ReadonlyArray<T>,
): T[] {
  return list.indexOf(v) >= 0 ? list.filter((x) => x !== v) : canonical([...list, v], vocab);
}

/**
 * Geloofschip aan/uit: "Geen geloof" ([]) sluit de andere uit en omgekeerd; "Een ander geloof"
 * combineert. `none` = de chip "Geen geloof".
 */
export function toggleFaith(faiths: Faith[] | null, v: Faith | 'none'): Faith[] | null {
  if (v === 'none') return faiths !== null && faiths.length === 0 ? null : [];
  const cur = faiths ?? [];
  const next = toggle(cur, v, FAITHS);
  return next.length === 0 ? null : next;
}

export function toggleTradition(list: ReadonlyArray<Tradition>, v: Tradition): Tradition[] {
  return toggle(list, v, TRADITIONS);
}

/** "Bidden · Wudu doen · Moskee(les) · Koranles": wat het gekozen geloof zichtbaar maakt. */
export function practicesLine(faiths: ReadonlyArray<Faith> | null, a: AppMessages): string {
  if (!faiths || faiths.length === 0) return '';
  const ids: string[] = [];
  for (const f of faiths)
    for (const id of FAITH_PRACTICES[f]) if (ids.indexOf(id) < 0) ids.push(id);
  const names = a.profile.screens.practices as Record<string, string>;
  return ids.map((id) => names[id] ?? id).join(' · ');
}

/** Een lijst namen als zin: "Baba, Anne en Yakub". */
export function joinNames(names: ReadonlyArray<string>, and: string): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} ${and} ${names[names.length - 1] ?? ''}`;
}

/**
 * Tweede regel onder een lid (§8.P.4): "Volgt het gezin", "Volgt het gezin, plus vegetarisch",
 * "Volgt het gezin, zonder geen alcohol", "Geloof: Christendom". Nooit op de kiosk.
 */
export function memberSummary(
  override: {
    faiths: string[] | null;
    food_rules_extra: string[];
    food_rules_skip: string[];
  } | null,
  a: AppMessages,
): string {
  const p = a.profile;
  if (!override) return p.member.follows;
  const food = p.food.labels as Record<string, string>;
  const faithNames = p.faiths.labels as Record<string, string>;
  const parts: string[] = [];
  if (override.faiths !== null) {
    const list =
      override.faiths.length === 0
        ? p.faiths.none
        : override.faiths.map((f) => faithNames[f] ?? f).join(', ');
    parts.push(t(p.member.faithDiffers, { what: list }));
  }
  const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
  if (override.food_rules_extra.length > 0) {
    parts.push(
      t(p.member.plus, {
        what: override.food_rules_extra.map((r) => lower(food[r] ?? r)).join(', '),
      }),
    );
  }
  if (override.food_rules_skip.length > 0) {
    parts.push(
      t(p.member.minus, {
        what: override.food_rules_skip.map((r) => lower(food[r] ?? r)).join(', '),
      }),
    );
  }
  if (parts.length === 0) return p.member.follows;
  // "Volgt het gezin, plus vegetarisch" + "zonder ...": één zin, zonder "Volgt het gezin" dubbel.
  return parts.map((s, i) => (i === 0 ? s : s.replace(`${p.member.follows}, `, ''))).join('; ');
}
